import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import api from '../../api';
import defaultProfilePic from '../../assets/defaultProfilePic.png';
import { getProfilePictureUrl } from '../../utils/apiHelper';
import countryCodes from '../../data/country-codes.json';
import './ProfileSetup.css';

const ProfileSetup = () => {
  const navigate = useNavigate();
  const { token, profile, fetchUserProfile } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    countryCode: '+91',
    interestedCategories: [],
    interestedSkills: [],
  });
  const [profilePicture, setProfilePicture] = useState(null);
  const [preview, setPreview] = useState(defaultProfilePic);
  const [categories, setCategories] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedParent, setSelectedParent] = useState('');
  const [selectedSkillGroup, setSelectedSkillGroup] = useState('');

  const popularSkills = {
    'Software': ['MERN Stack', 'ReactJS', 'MongoDB', 'Node.Js', 'Microsoft Power BI', 'Microsoft Excel', 'Tableau'],
    'Programming Languages': ['Python', 'JavaScript', 'Java', 'C++', 'C#', 'Go', 'Swift', 'Kotlin', 'TypeScript', 'Ruby'],
    'Web Development': ['HTML', 'CSS', 'Next.js', 'Angular', 'Vue.js', 'Django', 'Flask', 'Spring Boot', 'ASP.NET'],
    'Data Science': ['Machine Learning', 'Deep Learning', 'Pandas', 'NumPy', 'Scikit-learn', 'TensorFlow', 'Keras', 'PyTorch', 'R'],
    'Cloud Computing': ['AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes'],
    'Mobile Development': ['React Native', 'Flutter', 'Android Development', 'iOS Development'],
    'Cybersecurity': ['Ethical Hacking', 'Network Security', 'Penetration Testing', 'Security Analytics'],
    'Design': ['UI/UX Design', 'Figma', 'Adobe XD', 'Photoshop', 'Illustrator'],
    'Marketing': ['Digital Marketing', 'SEO', 'Content Marketing', 'Social Media Marketing', 'Email Marketing'],
    'Project Management': ['Agile', 'Scrum', 'PMP', 'Jira', 'Asana'],
    'Business': ['Financial Modeling', 'Business Strategy', 'Entrepreneurship', 'Sales', 'Negotiation'],
    'Human Resources': ['Recruitment', 'Talent Management', 'HR Analytics', 'Compensation & Benefits'],
    'Education': ['Instructional Design', 'Curriculum Development', 'E-learning'],
    'Writing': ['Copywriting', 'Technical Writing', 'Creative Writing', 'Grant Writing'],
    'Finance & Accounting': ['Bookkeeping', 'Financial Planning', 'Tax Preparation', 'Auditing'],
    'Legal': ['Legal Research', 'Contract Law', 'Intellectual Property'],
    'Art': ['Drawing', 'Painting', 'Sculpting', 'Digital Art'],
  };

  useEffect(() => {
    const authToken = token || localStorage.getItem('authToken');
    if (!authToken) {
      navigate('/login');
      return;
    }

    // If profile isn't loaded yet, fetch it first
    if (!profile) {
      fetchUserProfile(authToken);
      return;
    }

    // Pre-fill from profile (Google users will have name + picture from Google)
    setFormData((prev) => ({
      ...prev,
      name: profile.name || '',
      phone: profile.phone || '',
      countryCode: profile.countryCode || '+91',
    }));
    const profileImageUrl = getProfilePictureUrl(profile.profilePicture, api.img, defaultProfilePic, profile.name);
    setPreview(profileImageUrl);
  }, [token, profile, navigate, fetchUserProfile]);

  // Fetch categories for interest selection
  useEffect(() => {
    const fetchCategories = async () => {
      const authToken = token || localStorage.getItem('authToken');
      if (!authToken) return;
      try {
        const response = await axios.get(api.getAllCategories, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const cats = response.data.categories || response.data || [];
        // Extract all subcategories grouped by parent category
        const grouped = cats.map((cat) => {
          const subs = [];
          (cat.subcategories || []).forEach((sub) => {
            subs.push(sub.name);
            (sub.subcategories || []).forEach((subsub) => {
              subs.push(subsub.name);
            });
          });
          return { name: cat.name, subcategories: subs };
        });
        setCategories(grouped);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePicture(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const toggleCategory = (categoryName) => {
    setFormData((prev) => {
      const selected = prev.interestedCategories;
      if (selected.includes(categoryName)) {
        return { ...prev, interestedCategories: selected.filter((c) => c !== categoryName) };
      }
      return { ...prev, interestedCategories: [...selected, categoryName] };
    });
    setSelectedParent('');
  };

  const toggleSkill = (skill) => {
    setFormData((prev) => {
      const selected = prev.interestedSkills;
      if (selected.includes(skill)) {
        return { ...prev, interestedSkills: selected.filter((s) => s !== skill) };
      }
      return { ...prev, interestedSkills: [...selected, skill] };
    });
    setSelectedSkillGroup('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Please enter your name.');
      return;
    }

    if (!formData.phone.trim()) {
      toast.error('Please enter your phone number.');
      return;
    }

    // Basic phone validation
    const phoneRegex = /^\d{7,15}$/;
    if (!phoneRegex.test(formData.phone.trim())) {
      toast.error('Please enter a valid phone number (7-15 digits).');
      return;
    }

    try {
      setIsSubmitting(true);
      const data = new FormData();
      data.append('name', formData.name.trim());
      data.append('phone', formData.phone.trim());
      data.append('countryCode', formData.countryCode);
      data.append('interestedCategories', JSON.stringify(formData.interestedCategories));
      data.append('interestedSkills', JSON.stringify(formData.interestedSkills));

      if (profilePicture instanceof File) {
        data.append('profilePicture', profilePicture);
      }

      const authToken = token || localStorage.getItem('authToken');
      await axios.put(api.completeProfile, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${authToken}`,
        },
      });

      toast.success('Profile setup completed!');
      await fetchUserProfile(authToken);
      navigate('/home');
    } catch (error) {
      console.error('Error completing profile:', error);
      toast.error(error?.response?.data?.message || 'Failed to complete profile setup.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="profile-setup-bg">
      <div className="profile-setup-container">
        <div className="profile-setup-header">
          <h2>Complete Your Profile</h2>
          <p>Set up your profile to get the best learning experience</p>
        </div>

        <form onSubmit={handleSubmit} className="profile-setup-form">
          {/* Profile Picture */}
          <div className="profile-setup-picture-section">
            <label htmlFor="profilePictureInput" className="profile-setup-avatar-wrapper">
              <img src={preview} alt="Profile" className="profile-setup-avatar" />
              <div className="profile-setup-camera-overlay">
                <span className="material-icons">photo_camera</span>
              </div>
            </label>
            <input
              id="profilePictureInput"
              type="file"
              onChange={handleFileChange}
              accept="image/jpeg,image/png,image/jpg"
              style={{ display: 'none' }}
            />
            <p className="profile-setup-picture-hint">Click to upload profile photo</p>
          </div>

          {/* Full Name */}
          <div className="profile-setup-field">
            <label htmlFor="name">Full Name <span className="required">*</span></label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              required
            />
          </div>

          {/* Email (read-only) */}
          <div className="profile-setup-field">
            <label htmlFor="email">
              Email Address
              <span className="verified-badge">Verified</span>
            </label>
            <input
              type="email"
              id="email"
              value={profile?.email || ''}
              disabled
              className="profile-setup-disabled-input"
            />
          </div>

          {/* Phone Number with Country Code */}
          <div className="profile-setup-field">
            <label htmlFor="phone">Contact Number <span className="required">*</span></label>
            <div className="profile-setup-phone-group">
              <select
                name="countryCode"
                value={formData.countryCode}
                onChange={handleChange}
                className="profile-setup-country-select"
              >
                {countryCodes.map((country) => (
                  <option key={country.code + country.name} value={country.code}>
                    {country.code} ({country.name})
                  </option>
                ))}
              </select>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter your phone number"
                className="profile-setup-phone-input"
                required
              />
            </div>
          </div>

          {/* Interested Categories */}
          <div className="profile-setup-field">
            <label>What courses are you interested in?</label>
            <p className="profile-setup-field-hint">Choose a category, then select subcategories</p>

            {/* Selected tags */}
            {formData.interestedCategories.length > 0 && (
              <div className="profile-setup-selected-tags">
                {formData.interestedCategories.map((item) => (
                  <span key={item} className="profile-setup-tag">
                    {item}
                    <button type="button" onClick={() => toggleCategory(item)}>&times;</button>
                  </span>
                ))}
              </div>
            )}

            {/* Parent category dropdown */}
            <select
              value={selectedParent}
              onChange={(e) => setSelectedParent(e.target.value)}
              className="profile-setup-category-dropdown"
            >
              <option value="">-- Select a category --</option>
              {categories.map((group) => (
                <option key={group.name} value={group.name}>{group.name}</option>
              ))}
            </select>

            {/* Subcategory chips for selected parent */}
            {selectedParent && (
              <div className="profile-setup-category-chips">
                {(categories.find((g) => g.name === selectedParent)?.subcategories || []).map((sub) => (
                  <button
                    key={sub}
                    type="button"
                    className={`profile-setup-category-chip ${
                      formData.interestedCategories.includes(sub) ? 'selected' : ''
                    }`}
                    onClick={() => toggleCategory(sub)}
                  >
                    {sub}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Skills Selection */}
          <div className="profile-setup-field">
            <label>What skills do you want to learn?</label>
            <p className="profile-setup-field-hint">Choose a skill group, then select skills</p>

            {/* Selected skill tags */}
            {formData.interestedSkills.length > 0 && (
              <div className="profile-setup-selected-tags">
                {formData.interestedSkills.map((skill) => (
                  <span key={skill} className="profile-setup-tag profile-setup-tag-skill">
                    {skill}
                    <button type="button" onClick={() => toggleSkill(skill)}>&times;</button>
                  </span>
                ))}
              </div>
            )}

            {/* Skill group dropdown */}
            <select
              value={selectedSkillGroup}
              onChange={(e) => setSelectedSkillGroup(e.target.value)}
              className="profile-setup-category-dropdown"
            >
              <option value="">-- Select a skill group --</option>
              {Object.keys(popularSkills).map((group) => (
                <option key={group} value={group}>{group}</option>
              ))}
            </select>

            {/* Skill chips for selected group */}
            {selectedSkillGroup && (
              <div className="profile-setup-category-chips">
                {(popularSkills[selectedSkillGroup] || []).map((skill) => (
                  <button
                    key={skill}
                    type="button"
                    className={`profile-setup-category-chip ${
                      formData.interestedSkills.includes(skill) ? 'selected' : ''
                    }`}
                    onClick={() => toggleSkill(skill)}
                  >
                    {skill}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            className="profile-setup-submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Complete Setup'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProfileSetup;
